import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { ReactNode } from 'react'

import { randomId } from '@/core/utils/random'

type ModalItem = {
  id: string
  title?: string
  content: ReactNode
  onClose?: () => void
  closing?: boolean
}

const MODAL_BASE_Z_INDEX = 5000000

type ModalState = {
  modals: ModalItem[]
  modalBaseZIndex: number
}

type ModalActions = {
  addModal: (modal: Omit<ModalItem, 'id' | 'closing'>) => string
  removeModal: (id: string) => void
  finishCloseModal: (id: string) => void
  clearModals: () => void
}

export const useModalStore = create<ModalState & ModalActions>()(
  devtools(
    (set) => ({
      modals: [],
      modalBaseZIndex: MODAL_BASE_Z_INDEX,

      addModal: (modal) => {
        const id = randomId()
        set((s) => ({
          modals: [...s.modals, { ...modal, id }],
        }))
        return id
      },

      removeModal: (id) =>
        set((s) => {
          const item = s.modals.find((modal) => modal.id === id)
          if (!item || item.closing) return s
          return {
            modals: s.modals.map((modal) =>
              modal.id === id ? { ...modal, closing: true } : modal
            ),
          }
        }),

      // Base UI의 닫힘 완료 콜백에서만 물리적으로 제거한다.
      finishCloseModal: (id) =>
        set((s) => ({
          modals: s.modals.filter((modal) => modal.id !== id || !modal.closing),
        })),

      // 화면 종료·초기화 시 전체 스택을 즉시 폐기하는 API다.
      clearModals: () => set({ modals: [] }),
    }),
    { name: 'modal-store' }
  )
)
